import { IssueTaskDeliveryInfo as IssueTaskDeliveryInfoType, IssueTaskDoc } from '@gooduncles/gu-app-schema';
import { Input, message, notification } from 'antd';
import { FC } from 'react';

import { errorObjectToString } from 'src/utils/common';
import {
  addImageToIssueTaskDeliveryInfo,
  removeImageFromIssueTaskDeliveryInfo,
  updateIssueTaskNestedFields,
} from 'src/utils/firebase/firebase-short-cut';
import { UploadImageResult } from 'src/utils/image-util';

import useDebouncedUpdate from 'src/hooks/useDebouncedUpdate';

import IssueCardSubTitle from 'src/atomic-components/atoms/IssueCardItem/IssueCardSubTitle';
import { InfoItem } from 'src/atomic-components/atoms/IssueInfoItem';

import styles from './IssueTaskCard.module.scss';

import ImageUploadItem from '../IssueImageUploadItem';
import StoreSearch from '../StoreSearch/StoreSearch';

interface IssueTaskDeliveryInfoProps {
  issueTaskDoc: IssueTaskDoc;
  deliveryInfo: IssueTaskDeliveryInfoType;
  disabled: boolean;
}
const IssueTaskDeliveryInfo: FC<IssueTaskDeliveryInfoProps> = ({ issueTaskDoc, deliveryInfo, disabled }) => {
  const images = deliveryInfo.images ?? {};
  const [memo, setMemo] = useDebouncedUpdate(
    issueTaskDoc.deliveryInfo?.memo ?? '',
    async (value: string) => {
      try {
        await updateIssueTaskNestedFields(issueTaskDoc._id, { deliveryInfo: { memo: value || null } });
      } catch (error) {
        console.error('Failed to update deliveryInfo.memo:', error);
      }
    },
    1000
  );

  const onStoreSelect = async (storeId: string | null, storeName: string) => {
    try {
      if (!storeId) {
        throw new Error('목적지를 선택해주세요.');
      }
      await updateIssueTaskNestedFields(issueTaskDoc._id, {
        deliveryInfo: {
          storeId,
          storeName,
        },
      });
      message.success('목적지를 변경했습니다.');
    } catch (error) {
      console.error(error);
      notification.error({
        message: '목적지 수정 실패',
        description: errorObjectToString(error),
      });
    }
  };

  const onImageUploadSuccess = async (result: UploadImageResult, index: number) => {
    try {
      await addImageToIssueTaskDeliveryInfo(issueTaskDoc._id, result.imageFileId, {
        ...result.result,
        index,
      });
      message.success('이미지 업로드 성공');
    } catch (error) {
      console.error(error);
      notification.error({
        message: '이미지 업로드 실패',
        description: errorObjectToString(error),
      });
    }
  };

  const onImageDeleteSuccess = async (imageId: string) => {
    try {
      await removeImageFromIssueTaskDeliveryInfo(issueTaskDoc._id, imageId);
    } catch (error) {
      console.error('Failed to update images after deletion:', error);
      notification.error({
        message: '이미지 삭제 후 업데이트 실패',
        description: errorObjectToString(error),
      });
    }
  };

  return (
    <div>
      <IssueCardSubTitle text='목적지' />
      <div className={styles.issueTaskItem}>
        <StoreSearch
          selectedStoreId={issueTaskDoc.deliveryInfo?.storeId}
          onStoreSelect={onStoreSelect}
          disabled={disabled}
        />
      </div>
      <InfoItem
        label='특이사항'
        value={
          disabled ? (
            <span>{memo}</span>
          ) : (
            <Input
              size='small'
              disabled={disabled}
              placeholder='태스크 내용을 제외한 입력사항'
              value={memo}
              allowClear
              onChange={(e) => setMemo(e.target.value)}
              style={{ width: 216 }}
            />
          )
        }
      />
      {disabled && Object.keys(images).length > 0 ? (
        <InfoItem label='참고 이미지' value={<span>참고 이미지가 없습니다.</span>} />
      ) : (
        <ImageUploadItem
          label='참고 이미지'
          maxCount={3}
          images={images}
          owner={{
            entity: 'issueTask',
            caseId: issueTaskDoc.issueCaseId,
            taskId: issueTaskDoc._id,
          }}
          onUploadSuccess={onImageUploadSuccess}
          onDeleteSuccess={onImageDeleteSuccess}
          disabled={disabled}
        />
      )}
      <InfoItem label='담당자' value={issueTaskDoc.deliveryInfo?.deliveryPartnerName ?? '-'} />
    </div>
  );
};

export default IssueTaskDeliveryInfo;
